/** Escape text for safe HTML insertion. */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const PAGE_HEADER_RE = /^Page \d+ of \d+\s*$/gm;

const KNOWN_SECTION_HEADINGS = new Set([
  'role summary',
  'role overview',
  'key responsibilities',
  'responsibilities',
  'required skills and experience',
  'required skills',
  'required qualifications',
  'preferred qualifications',
  'minimum qualifications',
  'qualifications',
  'work experience',
  'skills',
  'key skills',
  'technical skills',
  'soft skills',
  'core competencies',
  'education',
  'compensation',
  'position summary',
  'essential functions',
  'about feuji',
  'non-negotiables',
  'key performance',
  'key performance indicators',
  'psychometric',
  'job description',
  'requirements',
  'duties',
  'key duties',
  'what you will do',
  'what we offer',
  'benefits',
]);

/** Sections whose body lines should render as bullet lists even without • prefix. */
const LIST_SECTION_HEADINGS = new Set([
  'key responsibilities',
  'responsibilities',
  'required skills and experience',
  'required skills',
  'required qualifications',
  'preferred qualifications',
  'minimum qualifications',
  'qualifications',
  'education',
  'work experience',
  'skills',
  'key skills',
  'technical skills',
  'soft skills',
  'core competencies',
  'essential functions',
  'non-negotiables',
  'requirements',
  'duties',
  'key duties',
  'what you will do',
  'benefits',
]);

/** Detail labels used inside sections (Education, Work Experience) — not document header meta. */
const SECTION_DETAIL_LABELS = new Set([
  'minimum',
  'preferred',
  'minimum experience',
  'maximum experience',
  'minimum education',
  'preferred education',
  'required',
  'optional',
  'description',
]);

/** Known JD header field labels — Organization, Role, Location, etc. */
const KNOWN_META_LABELS = new Set([
  'job title', 'title', 'role', 'position', 'position title', 'job role',
  'company', 'organization', 'organisation', 'employer', 'company name',
  'mode of hire', 'employment type', 'position type', 'hire type',
  'experience', 'years of experience', 'experience required',
  'work location', 'location', 'preferred location', 'job location', 'workplace',
  'department', 'job code', 'client', 'client name', 'reporting to',
]);

const META_LABEL_BLOCKLIST = /^(description|summary|overview|responsibilities|requirements|qualifications|skills|duties|benefits|education|compensation|about|notes?|disclaimer)$/i;

function isMetaLabelKey(labelKey) {
  if (!labelKey) return false;
  const key = labelKey.trim().toLowerCase();
  if (KNOWN_META_LABELS.has(key)) return true;
  if (SECTION_DETAIL_LABELS.has(key)) return false;
  if (META_LABEL_BLOCKLIST.test(key)) return false;
  if (KNOWN_SECTION_HEADINGS.has(key)) return false;
  if (/^about\s+/i.test(key)) return false;
  if (/^(minimum|preferred|required|optional|description)\b/i.test(key)) return false;
  return key.length <= 45 && key.split(/\s+/).length <= 4;
}

/** Parse `Label: value` header lines (Organization, Role, Location, etc.). */
export function parseJdMetaLine(line) {
  const trimmed = String(line || '').trim();
  const match = trimmed.match(/^([^:\n]+):\s*(.*)$/s);
  if (!match) return null;

  const label = match[1].trim();
  const value = match[2] ?? '';
  if (!label || label.length > 60) return null;
  if (/[.!?]/.test(label)) return null;
  if (label.split(/\s+/).length > 6) return null;
  if (!isMetaLabelKey(label)) return null;

  return { label, value };
}

export function isJdMetaLine(line) {
  return parseJdMetaLine(line) != null;
}

function stripPageHeaders(text) {
  return String(text || '')
    .replace(PAGE_HEADER_RE, '')
    .replace(/\bPage \d+ of \d+\b/g, '')
    .replace(/^(Job Description\s*\n?){2,}/gm, '')
    .replace(/^Job Description\s*\n?/gm, '');
}

function decodeHtmlEntities(text) {
  let out = String(text || '');
  for (let i = 0; i < 3; i += 1) {
    const next = out
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&#x27;/gi, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&bull;/gi, '•')
      .replace(/&#8226;/g, '•')
      .replace(/&middot;/gi, '·')
      .replace(/&#183;/g, '·')
      .replace(/&rsquo;|&#8217;/g, '’')
      .replace(/&lsquo;|&#8216;/g, '‘')
      .replace(/&rdquo;|&#8221;/g, '”')
      .replace(/&ldquo;|&#8220;/g, '“')
      .replace(/&ndash;|&#8211;/g, '–')
      .replace(/&mdash;|&#8212;/g, '—');
    if (next === out) break;
    out = next;
  }
  return out;
}

export function hasHtmlMarkup(text) {
  return /<\/?[a-z][\s\S]*?>/i.test(String(text || ''));
}

/** True when content carries HTML or markdown formatting that must not be plain-text reformatted. */
export function isJdRichTextContent(text) {
  if (!text) return false;
  const raw = String(text).trim();
  if (!raw) return false;
  if (hasHtmlMarkup(raw)) return true;
  const decoded = decodeHtmlEntities(raw);
  if (hasHtmlMarkup(decoded)) return true;
  return (
    /\*\*[^*\n]+\*\*/.test(raw)
    || /__[^_\n]+__/.test(raw)
    || /(?<!\*)\*[^*\n]+\*(?!\*)/.test(raw)
    || /^\s*[-•·*]\s+\S/m.test(raw)
    || /^\s*\d+[.)]\s+\S/m.test(raw)
  );
}

const JD_ALLOWED_TAGS = new Set([
  'P', 'DIV', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'DEL',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'SPAN', 'HR', 'SUP', 'SUB',
]);

function sanitizeJdHtml(html) {
  return String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*(".*?"|'.*?')/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/<\/?o:p[^>]*>/gi, '');
}

function replaceElementTag(el, newTag) {
  const doc = el.ownerDocument;
  const next = doc.createElement(newTag);
  while (el.firstChild) next.appendChild(el.firstChild);
  [...el.attributes].forEach((attr) => {
    if (attr.name.toLowerCase() === 'style') next.setAttribute(attr.name, attr.value);
  });
  el.parentNode?.replaceChild(next, el);
  return next;
}

function extractTextAlign(el) {
  const style = el.getAttribute('style') || '';
  const fromStyle = style.match(/text-align\s*:\s*(left|right|center|justify)/i);
  if (fromStyle) return fromStyle[1].toLowerCase();
  const align = (el.getAttribute('align') || '').toLowerCase();
  if (['left', 'right', 'center', 'justify'].includes(align)) return align;
  return '';
}

function extractFontWeightStyle(style) {
  if (/font-weight\s*:\s*(bold|[7-9]00)/i.test(style)) return 'font-weight:700';
  if (/font-style\s*:\s*italic/i.test(style)) return 'font-style:italic';
  if (/text-decoration\s*:\s*underline/i.test(style)) return 'text-decoration:underline';
  return '';
}

/** Sanitize and normalize HTML so alignment, emphasis, and lists render in preview. */
export function sanitizeAndNormalizeJdHtml(html) {
  const input = sanitizeJdHtml(String(html || ''));
  if (!input.trim()) return '';
  if (typeof DOMParser === 'undefined') return input;

  const doc = new DOMParser().parseFromString(`<div id="__jd_root__">${input}</div>`, 'text/html');
  const root = doc.getElementById('__jd_root__');
  if (!root) return input;

  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const el = child;
      const tag = el.tagName;

      if (!JD_ALLOWED_TAGS.has(tag)) {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
        return;
      }

      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || name === 'href' || name === 'src' || name === 'class' || name === 'id') {
          el.removeAttribute(attr.name);
        }
      });

      const textAlign = extractTextAlign(el);
      const weightStyle = extractFontWeightStyle(el.getAttribute('style') || '');
      if (textAlign) {
        el.setAttribute('style', `text-align:${textAlign}`);
      } else if (weightStyle) {
        el.setAttribute('style', weightStyle);
      } else {
        el.removeAttribute('style');
      }
      el.removeAttribute('align');

      let current = el;
      if (tag === 'B') current = replaceElementTag(el, 'strong');
      else if (tag === 'I') current = replaceElementTag(el, 'em');

      walk(current);
    });
  };

  walk(root);
  return normalizeSectionListsInHtml(enhanceMetaLabelsInHtml(root.innerHTML.trim()));
}

function isHeadingElement(el) {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (!text) return false;
  if (el.classList?.contains('jd-preview-heading')) return true;
  if (/^H[1-6]$/i.test(el.tagName)) return true;

  const markdownHeading = text.match(/^\*\*(.+?)\*\*:?\s*$/);
  if (markdownHeading && isSectionHeading(markdownHeading[1])) return true;

  if (isSectionHeading(text)) return true;

  if (el.tagName === 'P' && el.children.length === 1 && el.children[0].tagName === 'STRONG') {
    const strongText = (el.children[0].textContent || '').trim();
    if (strongText && isSectionHeading(strongText)) return true;
  }

  return false;
}

function normalizeHeadingElement(el, doc) {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  const markdownHeading = text.match(/^\*\*(.+?)\*\*:?\s*$/);
  const headingText = markdownHeading ? markdownHeading[1] : text.replace(/:$/, '');

  const p = doc.createElement('p');
  p.className = 'jd-preview-heading';
  p.innerHTML = `<strong>${inlineMarkdownToHtml(headingText)}</strong>`;
  const align = el.getAttribute('style') || '';
  if (align) p.setAttribute('style', align);
  return p;
}

function normalizeSectionListsInHtml(html) {
  if (!html || typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div id="__jd_norm__">${html}</div>`, 'text/html');
  const root = doc.getElementById('__jd_norm__');
  if (!root) return html;

  const output = doc.createElement('div');
  let activeSection = null;
  let pendingBullets = [];

  const flushBullets = () => {
    if (!pendingBullets.length) return;
    const ul = doc.createElement('ul');
    pendingBullets.forEach((item) => {
      const li = doc.createElement('li');
      li.innerHTML = inlineMarkdownToHtml(item);
      ul.appendChild(li);
    });
    output.appendChild(ul);
    pendingBullets = [];
  };

  [...root.childNodes].forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node;
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();

    if (isHeadingElement(el)) {
      flushBullets();
      const headingKey = normalizeHeadingKey(text);
      activeSection = isListSectionHeading(headingKey) || isListSectionHeading(text)
        ? headingKey
        : null;
      output.appendChild(normalizeHeadingElement(el, doc));
      return;
    }

    if (el.tagName === 'UL' || el.tagName === 'OL') {
      flushBullets();
      activeSection = null;
      output.appendChild(el.cloneNode(true));
      return;
    }

    if (activeSection && (el.tagName === 'P' || el.classList?.contains('jd-preview-meta'))) {
      const items = normalizeListSectionBullets(text, activeSection);
      if (items.length) {
        pendingBullets.push(...items);
        return;
      }
    }

    flushBullets();
    activeSection = null;
    output.appendChild(el.cloneNode(true));
  });

  flushBullets();
  return output.innerHTML.trim();
}

function enhanceMetaLabelsInHtml(html) {
  if (!html || typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div id="__jd_enhance__">${html}</div>`, 'text/html');
  const root = doc.getElementById('__jd_enhance__');
  if (!root) return html;

  root.querySelectorAll('p, li, div').forEach((el) => {
    if (el.children.length === 1 && el.children[0].tagName === 'STRONG') return;
    const text = (el.textContent || '').trim();
    if (!text) return;
    if (isSectionDetailLine(text)) return;

    const meta = parseJdMetaLine(text);
    if (!meta) return;

    const align = el.getAttribute('style') || '';
    el.className = 'jd-preview-meta';
    el.innerHTML = `<strong>${escapeHtml(meta.label)}:</strong> ${inlineMarkdownToHtml(meta.value)}`;
    if (align) el.setAttribute('style', align);
  });

  return root.innerHTML.trim();
}

/** Preserve semantic HTML from Ceipal/uploads instead of stripping to plain text. */
export function normalizeJdHtmlFromSource(html) {
  const decoded = decodeHtmlEntities(stripPageHeaders(String(html || '')))
    .replace(/\r\n|\r/g, '\n')
    .trim();
  if (!decoded) return '';
  if (hasHtmlMarkup(decoded)) return sanitizeAndNormalizeJdHtml(decoded);
  return decoded;
}

function inlineMarkdownToHtml(text) {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
}

export function normalizeHeadingKey(line) {
  return String(line || '')
    .trim()
    .replace(/^\*\*(.+?)\*\*:?\s*$/, '$1')
    .toLowerCase()
    .replace(/:$/, '');
}

export function isSectionHeading(line) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 80) return false;

  if (parseJdMetaLine(trimmed)) return false;

  const lower = normalizeHeadingKey(trimmed);
  if (KNOWN_SECTION_HEADINGS.has(lower)) return true;
  if (/^about\s+\S/i.test(trimmed) && !trimmed.includes('.')) return true;

  // "Key Skills", "Key Qualifications", etc.
  if (/^key\s+[a-z][a-z\s/&()-]+$/i.test(trimmed) && !trimmed.includes('.') && trimmed.length <= 50) {
    return true;
  }

  const colonIdx = trimmed.indexOf(':');
  if (colonIdx > 0) {
    const labelPart = trimmed.slice(0, colonIdx).trim();
    const valuePart = trimmed.slice(colonIdx + 1).trim();
    if (valuePart && isMetaLabelKey(labelPart)) return false;
    if (/^[A-Z]/.test(trimmed)) return true;
  }

  if (/^[A-Z][A-Z0-9\s/&()-]+$/.test(trimmed) && trimmed.length <= 60) return true;

  // Title-case short labels (e.g. Qualifications, Education Requirements)
  if (
    !/[.,;]/.test(trimmed)
    && trimmed.length <= 50
    && /^[A-Z][A-Za-z0-9\s/&()-]+$/.test(trimmed)
    && trimmed.split(/\s+/).length <= 5
    && !/^(the|and|or|for|with|from|this|that|will|must|should)\b/i.test(trimmed)
  ) {
    const words = trimmed.split(/\s+/);
    const looksLikeLabel = words.every((w) => /^[A-Z]/.test(w) || ['and', 'or', 'of', '&'].includes(w.toLowerCase()));
    if (looksLikeLabel && words.length <= 4) return true;
  }

  return false;
}

export function isListSectionHeading(line) {
  const key = normalizeHeadingKey(line);
  if (LIST_SECTION_HEADINGS.has(key)) return true;
  if (/^key\s+(skills|responsibilities|requirements|qualifications|duties)\b/i.test(key)) return true;
  return false;
}

/** True when a `Label: value` line belongs inside a section list, not the JD header block. */
export function isSectionDetailLine(line) {
  const trimmed = String(line || '').trim();
  const match = trimmed.match(/^([^:\n]+):\s*(.*)$/s);
  if (!match) return false;
  const label = match[1].trim().toLowerCase();
  if (SECTION_DETAIL_LABELS.has(label)) return true;
  return /^(minimum|preferred|required|optional|description)\b/i.test(match[1].trim());
}

/** Normalize body lines under Education / Work Experience into consistent bullet text. */
export function normalizeListSectionBullets(line, sectionKey) {
  const stripped = String(line || '').trim().replace(/^[-•·*]\s+/, '').replace(/^\d+[.)]\s+/, '');
  if (!stripped) return [];

  if (isSectionDetailLine(stripped)) {
    return [stripped];
  }

  const key = normalizeHeadingKey(sectionKey || '');

  if (key === 'work experience') {
    const yearLead = stripped.match(/^(\d+)\s*(?:\+|\s*[-–]\s*(\d+))?\s*years?\b/i);
    if (yearLead) {
      const minY = yearLead[1];
      const maxY = yearLead[2];
      const expLabel = maxY
        ? `Minimum Experience: ${minY}-${maxY} Years`
        : `Minimum Experience: ${minY} Year${minY === '1' ? '' : 's'}`;
      const afterYears = stripped
        .replace(/^(\d+)\s*(?:\+|\s*[-–]\s*(\d+))?\s*years?\s*(?:of\s+)?(?:experience\s+)?(?:in\s+)?/i, '')
        .trim();
      if (afterYears && afterYears !== stripped) {
        return [expLabel, `Description: ${afterYears}`];
      }
      return [expLabel, `Description: ${stripped}`];
    }

    if (!/:/.test(stripped)) {
      return [`Description: ${stripped}`];
    }
  }

  if (key === 'education' && /^(minimum|preferred)\b/i.test(stripped) && !stripped.includes(':')) {
    return [stripped];
  }

  return [stripped];
}

function formatMetaLine(line) {
  const meta = parseJdMetaLine(line);
  if (!meta) return null;
  return `<p class="jd-preview-meta"><strong>${inlineMarkdownToHtml(meta.label)}:</strong> ${inlineMarkdownToHtml(meta.value)}</p>`;
}

function parseBulletLine(line) {
  const trimmed = line.trim();
  const unordered = trimmed.match(/^[-•·*]\s*(.+)$/);
  if (unordered) return { type: 'ul', text: unordered[1] };

  const ordered = trimmed.match(/^\d+[.)]\s+(.+)$/);
  if (ordered) return { type: 'ol', text: ordered[1] };

  return null;
}

function plainTextToPreviewHtml(text) {
  let normalized = decodeHtmlEntities(stripPageHeaders(text))
    .replace(/^["']|["']$/g, '')
    .replace(/\\n/g, '\n')
    .replace(/\r\n|\r/g, '\n')
    .replace(/\u00b7/g, '·')
    .trim();

  const lines = normalized.split('\n');
  let html = '';
  let inUl = false;
  let inOl = false;
  let activeListSection = null;

  const closeLists = () => {
    if (inUl) { html += '</ul>'; inUl = false; }
    if (inOl) { html += '</ol>'; inOl = false; }
  };

  const openUl = () => {
    if (!inUl) { closeLists(); html += '<ul>'; inUl = true; }
  };

  const appendListItem = (itemText) => {
    openUl();
    html += `<li>${inlineMarkdownToHtml(itemText)}</li>`;
  };

  const appendListItems = (items) => {
    items.forEach((item) => appendListItem(item));
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      closeLists();
      continue;
    }

    const markdownHeading = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    if (markdownHeading) {
      closeLists();
      activeListSection = isListSectionHeading(markdownHeading[1])
        ? normalizeHeadingKey(markdownHeading[1])
        : null;
      html += `<p class="jd-preview-heading"><strong>${inlineMarkdownToHtml(markdownHeading[1])}</strong></p>`;
      continue;
    }

    if (isSectionHeading(line)) {
      closeLists();
      activeListSection = isListSectionHeading(line) ? normalizeHeadingKey(line) : null;
      html += `<p class="jd-preview-heading"><strong>${inlineMarkdownToHtml(line.replace(/:$/, ''))}</strong></p>`;
      continue;
    }

    if (activeListSection) {
      const bullet = parseBulletLine(line);
      if (bullet?.type === 'ul') {
        appendListItem(bullet.text);
        continue;
      }
      appendListItems(normalizeListSectionBullets(line, activeListSection));
      continue;
    }

    const meta = formatMetaLine(line);
    if (meta) {
      closeLists();
      html += meta;
      continue;
    }

    const bullet = parseBulletLine(line);
    if (bullet) {
      if (bullet.type === 'ul') {
        appendListItem(bullet.text);
      } else {
        closeLists();
        if (!inOl) { html += '<ol>'; inOl = true; }
        html += `<li>${inlineMarkdownToHtml(bullet.text)}</li>`;
      }
      continue;
    }

    closeLists();
    html += `<p>${inlineMarkdownToHtml(line)}</p>`;
  }

  closeLists();
  return html;
}

/** Normalize JD text for API storage without stripping rich formatting. */
export function normalizeJdForStorage(value) {
  if (value == null) return '';
  const decoded = decodeHtmlEntities(String(value))
    .replace(/^["']|["']$/g, '')
    .replace(/\\n/g, '\n')
    .replace(/\r\n|\r/g, '\n')
    .trim();
  if (!decoded) return '';
  if (hasHtmlMarkup(decoded)) {
    return sanitizeAndNormalizeJdHtml(decoded);
  }
  return decoded.replace(/\n{3,}/g, '\n\n').trim();
}

function stripHtmlToPlainText(text) {
  return String(text || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<h[1-6][^>]*>/gi, '\n\n')
    .replace(/<[^>]*>/g, '');
}

/** Collapse justified-text artifacts where each letter is separated by a space. */
function collapseSpacedLettersLine(line) {
  const tokens = line.trim().split(/\s+/);
  if (tokens.length < 4) return line;
  const singleCharCount = tokens.filter((t) => t.length === 1).length;
  if (singleCharCount / tokens.length >= 0.55) {
    return tokens.join('');
  }
  return line;
}

/**
 * Plain-text normalization for PDF/Word export — decodes entities, strips HTML,
 * and removes per-character spacing so output matches the on-screen preview.
 */
export function normalizeJdForExport(value) {
  if (value == null) return '';

  let text = stripPageHeaders(String(value));
  text = decodeHtmlEntities(text);
  text = stripHtmlToPlainText(text);
  text = text
    .replace(/^["']|["']$/g, '')
    .replace(/\\n/g, '\n')
    .replace(/\r\n|\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/\u200b/g, '');

  text = text
    .split('\n')
    .map((line) => collapseSpacedLettersLine(line))
    .join('\n');

  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Convert JD text to HTML that preserves headings, lists, emphasis, and alignment. */
export function formatJdPreviewHtml(text) {
  if (!text) return '';

  const raw = decodeHtmlEntities(stripPageHeaders(String(text))).trim();
  if (!raw) return '';

  if (hasHtmlMarkup(raw)) {
    return sanitizeAndNormalizeJdHtml(raw);
  }

  return normalizeSectionListsInHtml(enhanceMetaLabelsInHtml(plainTextToPreviewHtml(raw)));
}

/** Rich HTML used consistently for preview, Word, and PDF export. */
export function prepareJdRichHtml(text) {
  return formatJdPreviewHtml(text);
}

export const JD_PREVIEW_CONTENT_STYLE = {
  lineHeight: 1.75,
  color: 'var(--ds-text)',
  fontSize: 13,
  maxHeight: 400,
  overflowY: 'auto',
  textAlign: 'left',
};

export const JD_PREVIEW_INNER_CSS = `
.jd-preview-content { text-align: left; }
.jd-preview-content p { margin: 0 0 0.65em; }
.jd-preview-content p.jd-preview-meta { margin: 0 0 0.2em; }
.jd-preview-content p.jd-preview-meta + p.jd-preview-heading { margin-top: 0.85em; }
.jd-preview-content p.jd-preview-meta strong { font-weight: 700; }
.jd-preview-content p.jd-preview-heading {
  margin-top: 1em;
  margin-bottom: 0.5em;
  font-weight: 700;
  font-size: 1.05em;
  color: var(--ds-text, #111);
}
.jd-preview-content p.jd-preview-heading:first-child { margin-top: 0; }
.jd-preview-content ul, .jd-preview-content ol { margin: 0.25em 0 0.85em 1.25em; padding-left: 1.15em; }
.jd-preview-content ul { list-style-type: disc; }
.jd-preview-content ol { list-style-type: decimal; }
.jd-preview-content li { margin-bottom: 0.4em; line-height: 1.55; }
.jd-preview-content li > ul, .jd-preview-content li > ol { margin-top: 0.35em; margin-bottom: 0.35em; }
.jd-preview-content strong, .jd-preview-content b { font-weight: 700; }
.jd-preview-content em, .jd-preview-content i { font-style: italic; }
.jd-preview-content u { text-decoration: underline; }
.jd-preview-content s, .jd-preview-content strike, .jd-preview-content del { text-decoration: line-through; }
.jd-preview-content blockquote {
  margin: 0.5em 0 0.85em;
  padding-left: 1em;
  border-left: 3px solid var(--ds-border, #d2d2d2);
  color: var(--ds-text2, #444);
}
.jd-preview-content hr { border: none; border-top: 1px solid var(--ds-border, #d2d2d2); margin: 0.85em 0; }
.jd-preview-content h1, .jd-preview-content h2, .jd-preview-content h3,
.jd-preview-content h4, .jd-preview-content h5, .jd-preview-content h6 {
  margin: 0.9em 0 0.45em;
  font-weight: 700;
}
.jd-preview-content span[style*="font-weight:700"],
.jd-preview-content span[style*="font-weight: 700"],
.jd-preview-content span[style*="font-weight:bold"],
.jd-preview-content span[style*="font-weight: bold"] { font-weight: 700; }
.jd-preview-content span[style*="font-style:italic"],
.jd-preview-content span[style*="font-style: italic"] { font-style: italic; }
.jd-preview-content span[style*="text-decoration:underline"],
.jd-preview-content span[style*="text-decoration: underline"] { text-decoration: underline; }
.jd-preview-content [align="center"], .jd-preview-content [style*="text-align: center"],
.jd-preview-content [style*="text-align:center"] { text-align: center !important; }
.jd-preview-content [align="right"], .jd-preview-content [style*="text-align: right"],
.jd-preview-content [style*="text-align:right"] { text-align: right !important; }
.jd-preview-content [align="left"], .jd-preview-content [style*="text-align: left"],
.jd-preview-content [style*="text-align:left"] { text-align: left !important; }
.jd-preview-content [align="justify"], .jd-preview-content [style*="text-align: justify"],
.jd-preview-content [style*="text-align:justify"] { text-align: justify !important; }
`;
